/**
 * Truth-layer Orders daily projection — zeecore /orders/v1/*
 * Today/yesterday come from stored snapshots (a 202 "preparing" while one is
 * first built); other ranges are live Iceberg reads.
 */

import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import type { OrdersDailyAvailability } from '../utils/ordersDailySupport';
import type {
  OrdersUiPreferencesPatch,
  OrdersUiPreferencesResponse,
} from '../types/ordersUiPreferences';
import type {
  OrdersDailyProjectionSource,
  OrdersDailyRow,
  OrdersDailySeriesDay,
  OrdersDailySeriesResponse,
  OrdersDailySummary,
  OrdersPeriodGrain,
  OrdersPeriodsResponse,
  UnscheduledOpenLeg,
} from '../types/ordersDailyTruth';

function htmlProxyHint(status: number, what: string): Error {
  return new Error(
    `${what} returned HTML (${status}). Restart zeecore and ensure /orders is proxied (Vite dev) or set VITE_ZEECORE_BASE_URL.`,
  );
}

async function readJson(response: Response, what: string): Promise<unknown> {
  const text = await response.text();
  if (text.trimStart().startsWith('<')) {
    throw htmlProxyHint(response.status, what);
  }
  if (!response.ok) {
    try {
      const err = JSON.parse(text) as { error?: string; message?: string };
      throw new Error(err.error ?? err.message ?? `${what} failed (${response.status})`);
    } catch (parseErr) {
      if (parseErr instanceof Error && parseErr.message !== `${what} failed (${response.status})` && !parseErr.message.startsWith('Unexpected')) {
        throw parseErr;
      }
      throw new Error(`${what} failed (${response.status}) ${text.slice(0, 200)}`);
    }
  }
  return JSON.parse(text);
}

export interface OrdersDailyBundle {
  records: OrdersDailyRow[];
  summary: OrdersDailySummary;
  unscheduled: UnscheduledOpenLeg[];
  overdue: OrdersDailyRow[];
  stale: OrdersDailyRow[];
  /** The range's cancelled legs: listed in the orders table, counted nowhere. */
  cancelled: OrdersDailyRow[];
  projection?: OrdersDailyProjectionSource;
}

/** 202 from zeecore: this day's snapshot is being built; ask again after `retryAfterSeconds`. */
export interface OrdersDailyPreparing {
  preparing: true;
  retryAfterSeconds: number;
}

/** Gauges + table share the same shaped rows. */
export async function fetchOrdersDailyBundle(
  startDate: string,
  endDate: string,
  options?: { signal?: AbortSignal; includeBacklog?: boolean; includeStale?: boolean },
): Promise<OrdersDailyBundle | OrdersDailyPreparing> {
  const response = await authenticatedFetch(
    API_ENDPOINTS.ordersV1.daily(startDate, endDate, {
      includeSummary: true,
      includeBacklog: options?.includeBacklog,
      includeStale: options?.includeStale,
    }),
    { cache: 'no-store', signal: options?.signal },
  );
  if (response.status === 202) {
    const body = (await readJson(response, 'Orders daily')) as { retry_after_seconds?: number };
    return { preparing: true, retryAfterSeconds: body.retry_after_seconds ?? 30 };
  }
  const json = (await readJson(response, 'Orders daily')) as {
    records?: OrdersDailyRow[];
    unscheduled?: UnscheduledOpenLeg[];
    overdue?: OrdersDailyRow[];
    stale?: OrdersDailyRow[];
    cancelled?: OrdersDailyRow[];
    summary?: OrdersDailySummary;
    projection?: OrdersDailyProjectionSource;
  };
  if (!json.summary) {
    throw new Error('Orders daily response missing summary (includeSummary=1 required)');
  }
  const emptyRisk = {
    missed_am: { consignments: 0, lifts: 0, impact_gbp: null as number | null },
    at_risk_1700: {
      consignments: 0,
      lifts: 0,
      impact_gbp_min: null as number | null,
      impact_gbp_max: null as number | null,
    },
    booked_in_excluded: { consignments: 0, lifts: 0 },
    missed_am_codes: ['E'],
    at_risk_service_group: 'PREMIUM',
    at_risk_due_time: '17:00',
  };
  const normalise = (row: OrdersDailyRow): OrdersDailyRow => ({
    ...row,
    missed_am: row.missed_am === true,
    at_risk_1700: row.at_risk_1700 === true,
    booked_in_excluded: row.booked_in_excluded === true,
  });
  const records = (json.records ?? []).map(normalise);
  return {
    records,
    unscheduled: json.unscheduled ?? [],
    overdue: (json.overdue ?? []).map(normalise),
    stale: (json.stale ?? []).map(normalise),
    cancelled: (json.cancelled ?? []).map(normalise),
    ...(json.projection ? { projection: json.projection } : {}),
    summary: {
      ...json.summary,
      service_risk: json.summary.service_risk ?? emptyRisk,
    },
  };
}

/** Per-day slim summaries for trend/CSV — no consignment rows. */
export async function fetchOrdersDailySeries(
  startDate: string,
  endDate: string,
  options?: { signal?: AbortSignal },
): Promise<OrdersDailySeriesResponse | OrdersDailyPreparing> {
  const response = await authenticatedFetch(
    API_ENDPOINTS.ordersV1.dailySeries(startDate, endDate),
    { cache: 'no-store', signal: options?.signal },
  );
  if (response.status === 202) {
    const body = (await readJson(response, 'Orders daily series')) as { retry_after_seconds?: number };
    return { preparing: true, retryAfterSeconds: body.retry_after_seconds ?? 30 };
  }
  const json = (await readJson(response, 'Orders daily series')) as OrdersDailySeriesResponse;
  if (!json.days || !Array.isArray(json.days)) {
    throw new Error('Orders daily series response missing days[]');
  }
  return {
    organisation_id: json.organisation_id,
    start_date: json.start_date,
    end_date: json.end_date,
    days: json.days as OrdersDailySeriesDay[],
    stale_days: Array.isArray(json.stale_days) ? json.stale_days : [],
    unavailable_days: Array.isArray(json.unavailable_days) ? json.unavailable_days : [],
    refresh_requested: json.refresh_requested === true,
    ...(typeof json.retry_after_seconds === 'number' ? { retry_after_seconds: json.retry_after_seconds } : {}),
    ...(json.computed_at_range ? { computed_at_range: json.computed_at_range } : {}),
    ...(json.projection ? { projection: json.projection } : {}),
  };
}

/** Week / month / quarter / year figures ("due in period"), stored — never computed on the request. */
export async function fetchOrdersPeriods(
  grain: OrdersPeriodGrain,
  from: string,
  to: string,
  options?: { signal?: AbortSignal },
): Promise<OrdersPeriodsResponse> {
  const response = await authenticatedFetch(
    API_ENDPOINTS.ordersV1.periods(grain, from, to),
    { cache: 'no-store', signal: options?.signal },
  );
  const json = (await readJson(response, 'Order periods')) as OrdersPeriodsResponse;
  if (!Array.isArray(json.periods)) throw new Error('Order periods response missing periods[]');
  return {
    ...json,
    unavailable: Array.isArray(json.unavailable) ? json.unavailable : [],
    refresh_requested: json.refresh_requested === true,
    stale: json.stale === true,
  };
}

/** This user's saved Orders page layout. `saved: false` = never saved; the defaults apply. */
export async function fetchOrdersUiPreferences(options?: { signal?: AbortSignal }): Promise<OrdersUiPreferencesResponse> {
  const response = await authenticatedFetch(API_ENDPOINTS.ordersV1.uiPreferences(), {
    cache: 'no-store',
    signal: options?.signal,
  });
  return (await readJson(response, 'Orders layout')) as OrdersUiPreferencesResponse;
}

/** Save a change to this user's layout. The server merges it over what is stored. */
export async function saveOrdersUiPreferences(
  patch: OrdersUiPreferencesPatch,
  options?: { signal?: AbortSignal },
): Promise<OrdersUiPreferencesResponse> {
  const response = await authenticatedFetch(API_ENDPOINTS.ordersV1.uiPreferences(), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
    signal: options?.signal,
  });
  return (await readJson(response, 'Saving your Orders layout')) as OrdersUiPreferencesResponse;
}

/** Which orders view this org gets (zeecore decides; projections/orders-daily/availability.ts). */
export async function fetchOrdersDailyAvailability(options?: { signal?: AbortSignal }): Promise<OrdersDailyAvailability> {
  const response = await authenticatedFetch(API_ENDPOINTS.ordersV1.availability(), { signal: options?.signal });
  const json = (await readJson(response, 'Orders availability')) as OrdersDailyAvailability;
  if (json?.status !== 'available' && json?.status !== 'not_set_up' && json?.status !== 'not_supported') {
    throw new Error('Orders availability response missing status');
  }
  return json;
}
